import { z } from 'zod';
import { AUTOMATIC_SUPPORTED_UNITS_SCHEMA } from '@schuettflix/interfaces';
import { ProductConfiguration } from '@/shared/models/ContextDataModel/interfaces/entities/product-configuration.ts';
import { AutomaticProductsFormValues } from '@/shared/forms/AutomaticProductsForm/schema/getAutomaticProductsFormSchema.ts';
import { derivePricingStrategy } from '@/shared/forms/AutomaticProductsForm/utils/derivePricingStrategy.ts';
import { isTransportProduct } from '@/shared/domain/product/isTransportProduct.ts';
import { getSchemaId } from '@/shared/domain/product/getSchemaId.ts';
import { TRANSPORT_EXCHANGE_VALUE } from '@/shared/constants/transportExchange.ts';
import { LocationConfigurationSchema } from '@/modules/location/domain/LocationConfigurationSchema.ts';
import {
    VehicleClass,
    VehicleClassSchema,
} from '@/shared/models/ContextDataModel/interfaces/entities/vehicle-class.ts';
import { CarrierOrganizationIdSchema } from '@/shared/schemas/CarrierOrganizationIdSchema.ts';
import { getProductCategoryByProductType } from '@schuettflix/product-group-utils';
import { getAdditionalServiceSchema } from '@/shared/forms/AutomaticProductsForm/schema/getAdditionalServiceSchema.ts';

export function mapAutomaticProductsFormValues(values: AutomaticProductsFormValues): ProductConfiguration[] {
    const pricingStrategy = derivePricingStrategy(values.isSalesTransportIncluded, values.isPurchaseTransportIncluded);

    switch (pricingStrategy) {
        case 'FRANCO':
            return mapFranco(values);
        case 'MERCHANT':
            return mapMerchant(values);
        case 'STANDARD':
            return mapStandard(values);
    }
}

// ─── Strategy mappers ──────────────────────────────────────────────────────────

function mapFranco(values: AutomaticProductsFormValues): ProductConfiguration[] {
    assertFranco(values);

    return [
        makeMaterialConfig(values, {
            partnerOrganizationId: values.supplierOrganizationId,
            salesQuantity: values.salesMaterialQuantity,
            salesUnit: values.salesMaterialUnit,
            purchaseQuantity: values.purchaseMaterialQuantity,
            purchaseUnit: values.purchaseMaterialUnit,
        }),
        makeTransportConfig(values, {
            index: 2,
            isMainProduct: false,
            partnerOrganizationId: resolveCarrierOrganizationId(values.carrierOrganizationId),
            processedBy: resolveProcessedBy(values.carrierOrganizationId),
            // Sales transport is bundled into material → derive quantity/unit from material, price = 0
            salesQuantity: values.salesMaterialQuantity,
            salesUnit: values.salesMaterialUnit,
            salesPrice: 0,
            salesTaxClassId: values.salesMaterialTaxClassId,
            showZeroSalesPrice: false,
            // Purchase transport is separate → comes from the purchase transport fieldset
            purchaseQuantity: values.purchaseTransportQuantity,
            purchaseUnit: values.purchaseTransportUnit,
            purchasePrice: values.purchaseTransportPrice,
            purchaseTaxClassId: values.purchaseTransportTaxClassId,
            showZeroPurchasePrice: values.showZeroPurchaseTransportPrice,
        }),
        ...makeAdditionalServiceConfigs(values, 3),
    ];
}

function mapMerchant(values: AutomaticProductsFormValues): ProductConfiguration[] {
    assertMerchant(values);

    return [
        makeMaterialConfig(values, {
            partnerOrganizationId: values.supplierOrganizationId,
            salesQuantity: values.salesMaterialQuantity,
            salesUnit: values.salesMaterialUnit,
            purchaseQuantity: values.purchaseMaterialQuantity,
            purchaseUnit: values.purchaseMaterialUnit,
        }),
        makeTransportConfig(values, {
            index: 2,
            isMainProduct: false,
            partnerOrganizationId: values.supplierOrganizationId,
            processedBy: 'PARTNER',
            salesQuantity: values.salesMaterialQuantity,
            salesUnit: values.salesMaterialUnit,
            salesPrice: 0,
            salesTaxClassId: values.salesMaterialTaxClassId,
            showZeroSalesPrice: false,
            purchaseQuantity: values.purchaseMaterialQuantity,
            purchaseUnit: values.purchaseMaterialUnit,
            purchasePrice: 0,
            purchaseTaxClassId: values.purchaseMaterialTaxClassId,
            showZeroPurchasePrice: false,
        }),
        ...makeAdditionalServiceConfigs(values, 3),
    ];
}

function mapStandard(values: AutomaticProductsFormValues): ProductConfiguration[] {
    if (isTransportProduct(values.productType)) {
        assertStandardTransportOnly(values);

        return [
            makeTransportConfig(values, {
                index: 1,
                isMainProduct: true,
                partnerOrganizationId: resolveCarrierOrganizationId(values.carrierOrganizationId),
                processedBy: resolveProcessedBy(values.carrierOrganizationId),
                salesQuantity: values.salesTransportQuantity,
                salesUnit: values.salesTransportUnit,
                purchaseQuantity: values.purchaseTransportQuantity,
                purchaseUnit: values.purchaseTransportUnit,
                purchasePrice: values.purchaseTransportPrice,
                purchaseTaxClassId: values.purchaseTransportTaxClassId,
                showZeroPurchasePrice: values.showZeroPurchaseTransportPrice,
            }),
            ...makeAdditionalServiceConfigs(values, 2),
        ];
    } else {
        assertStandardMaterialAndTransport(values);

        return [
            makeMaterialConfig(values, {
                partnerOrganizationId: values.supplierOrganizationId,
                salesQuantity: values.salesMaterialQuantity,
                salesUnit: values.salesMaterialUnit,
                purchaseQuantity: values.purchaseMaterialQuantity,
                purchaseUnit: values.purchaseMaterialUnit,
            }),
            makeTransportConfig(values, {
                index: 2,
                isMainProduct: false,
                partnerOrganizationId: resolveCarrierOrganizationId(values.carrierOrganizationId),
                processedBy: resolveProcessedBy(values.carrierOrganizationId),
                salesQuantity: values.salesTransportQuantity,
                salesUnit: values.salesTransportUnit,
                purchaseQuantity: values.purchaseTransportQuantity,
                purchaseUnit: values.purchaseTransportUnit,
                purchasePrice: values.purchaseTransportPrice,
                purchaseTaxClassId: values.purchaseTransportTaxClassId,
                showZeroPurchasePrice: values.showZeroPurchaseTransportPrice,
            }),
            ...makeAdditionalServiceConfigs(values, 3),
        ];
    }
}

// ─── Builders ─────────────────────────────────────────────────────────────────

type MaterialConfigOptions = {
    index?: number;
    isMainProduct?: boolean;
    partnerOrganizationId: number | null;
    salesQuantity: number;
    salesUnit: NonNullable<AutomaticProductsFormValues['salesMaterialUnit']>;
    purchaseQuantity: number;
    purchaseUnit: NonNullable<AutomaticProductsFormValues['purchaseMaterialUnit']>;
};

function makeMaterialConfig(
    values: AutomaticProductsFormValues,
    {
        index = 1,
        isMainProduct = true,
        partnerOrganizationId,
        salesQuantity,
        salesUnit,
        purchaseQuantity,
        purchaseUnit,
    }: MaterialConfigOptions
): ProductConfiguration {
    return {
        id: values.materialIdentifier,
        index,
        isMainProduct,
        productType: values.productType,
        productName: values.productName,
        category: 'MATERIAL',
        processingType: 'AUTOMATIC',
        currencyCode: values.currencyCode,
        partnerOrganizationId,
        // Legacy
        quantity: salesQuantity,
        unit: salesUnit,
        // New split
        salesQuantity,
        salesUnit,
        purchaseQuantity,
        purchaseUnit,

        salesPrice: values.salesMaterialPrice ?? 0,
        purchasePrice: values.purchaseMaterialPrice ?? 0,
        salesTaxClassId: values.salesMaterialTaxClassId ?? '',
        purchaseTaxClassId: values.purchaseMaterialTaxClassId ?? '',
        showZeroSalesPrice: values.showZeroSalesMaterialPrice ?? false,
        showZeroPurchasePrice: values.showZeroPurchaseMaterialPrice ?? false,
        additionalProperties: {
            schemaId: getSchemaId(values.productType),
            name: values.productName,
            disposalPurpose: values.disposalPurpose,
            disposalCertificateNumber: values.disposalCertificateNumber,
            sellerProductId: values.sellerProduct?.sellerProductId,
            productDefinitionId: values.sellerProduct?.productDefinitionId,
        } as ProductConfiguration['additionalProperties'],
    };
}

type TransportConfigOptions = {
    index: number;
    isMainProduct: boolean;
    partnerOrganizationId: number | null;
    processedBy: 'TRANSPORT_EXCHANGE' | 'PARTNER';
    salesQuantity: number;
    salesUnit: NonNullable<AutomaticProductsFormValues['salesTransportUnit']>;
    purchaseQuantity: number;
    purchaseUnit: NonNullable<AutomaticProductsFormValues['purchaseTransportUnit']>;
    salesPrice?: number;
    salesTaxClassId?: string;
    showZeroSalesPrice?: boolean;
    purchasePrice?: number;
    purchaseTaxClassId?: string;
    showZeroPurchasePrice?: boolean;
};

function makeTransportConfig(
    values: AutomaticProductsFormValues,
    {
        index,
        isMainProduct,
        partnerOrganizationId,
        processedBy,
        salesQuantity,
        salesUnit,
        salesPrice,
        salesTaxClassId,
        showZeroSalesPrice,
        purchaseQuantity,
        purchaseUnit,
        purchasePrice,
        purchaseTaxClassId,
        showZeroPurchasePrice = false,
    }: TransportConfigOptions
): ProductConfiguration {
    const resolvedSalesPrice = salesPrice ?? values.salesTransportPrice ?? 0;
    const resolvedSalesTaxClassId = salesTaxClassId ?? values.salesTransportTaxClassId ?? '';
    const resolvedShowZeroSalesPrice = showZeroSalesPrice ?? values.showZeroSalesTransportPrice ?? false;

    return {
        id: values.transportIdentifier,
        index,
        isMainProduct,
        productType: 'TRANSPORT',
        productName: values.productName,
        category: 'TRANSPORT',
        processingType: 'AUTOMATIC',
        currencyCode: values.currencyCode,
        partnerOrganizationId,
        // Legacy
        quantity: salesQuantity,
        unit: salesUnit,
        // New split
        salesQuantity,
        salesUnit,
        purchaseQuantity,
        purchaseUnit,
        salesPrice: resolvedSalesPrice,
        purchasePrice: purchasePrice ?? 0,
        salesTaxClassId: resolvedSalesTaxClassId,
        purchaseTaxClassId: purchaseTaxClassId ?? '',
        showZeroSalesPrice: resolvedShowZeroSalesPrice,
        showZeroPurchasePrice,
        additionalProperties: {
            schemaId: getSchemaId('TRANSPORT', values.freightType),
            name: values.productName,
            locations: [
                { ...values.loadingLocation, type: 'LOADING' },
                { ...values.unloadingLocation, type: 'UNLOADING' },
            ],
            weighingLocation: values.weighingLocation,
            vehicleClass: values.vehicleClass,
            freightType: values.freightType,
            disposalPurpose: values.disposalPurpose,
            disposalCertificateNumber: values.disposalCertificateNumber,
            disposition: values.disposition,
            numberOfTransports: resolveNumberOfTransports(purchaseQuantity, purchaseUnit, values.vehicleClass),
            processedBy,
        } as ProductConfiguration['additionalProperties'],
    };
}

function makeAdditionalServiceConfigs(values: AutomaticProductsFormValues, startIndex: number): ProductConfiguration[] {
    const additionalServiceSchema = getAdditionalServiceSchema();

    return values.additionalServices.map((rawService, offset) => {
        const service = additionalServiceSchema.parse(rawService);

        return {
            id: service.identifier,
            index: startIndex + offset,
            isMainProduct: false,
            productType: service.productType,
            productName: service.productName,
            category: getProductCategoryByProductType(service.productType),
            processingType: 'AUTOMATIC',
            currencyCode: service.currencyCode,
            partnerOrganizationId: service.partnerOrganizationId,
            // Legacy
            quantity: service.salesQuantity,
            unit: service.salesUnit,
            // New split
            salesQuantity: service.salesQuantity,
            salesUnit: service.salesUnit,
            purchaseQuantity: service.purchaseQuantity,
            purchaseUnit: service.purchaseUnit,
            salesPrice: service.salesPrice,
            purchasePrice: service.purchasePrice,
            salesTaxClassId: service.salesTaxClassId,
            purchaseTaxClassId: service.purchaseTaxClassId,
            showZeroSalesPrice: service.showZeroSalesPrice,
            showZeroPurchasePrice: service.showZeroPurchasePrice,
            additionalProperties: {
                schemaId: getSchemaId(service.productType),
                name: service.productName,
            } as ProductConfiguration['additionalProperties'],
        };
    });
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function resolveCarrierOrganizationId(
    carrierOrganizationId: AutomaticProductsFormValues['carrierOrganizationId']
): number | null {
    if (!carrierOrganizationId || carrierOrganizationId === TRANSPORT_EXCHANGE_VALUE) {
        return null;
    }

    return carrierOrganizationId;
}

function resolveProcessedBy(
    carrierOrganizationId: AutomaticProductsFormValues['carrierOrganizationId']
): 'TRANSPORT_EXCHANGE' | 'PARTNER' {
    return carrierOrganizationId === TRANSPORT_EXCHANGE_VALUE ? 'TRANSPORT_EXCHANGE' : 'PARTNER';
}

function resolveNumberOfTransports(
    quantity: number | undefined,
    unit: string | undefined,
    vehicleClass?: VehicleClass
): number | undefined {
    if (unit === 'TRANSPORT') {
        return quantity;
    }

    if (quantity !== undefined && vehicleClass?.payloadInTons) {
        return Math.ceil(quantity / vehicleClass.payloadInTons);
    }

    return undefined;
}

// ─── Validation ───────────────────────────────────────────────────────────────
const locationFields = {
    loadingLocation: LocationConfigurationSchema,
    unloadingLocation: LocationConfigurationSchema,
};

const materialSalesFields = {
    salesMaterialQuantity: z.number(),
    salesMaterialUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options),
    salesMaterialPrice: z.number(),
    salesMaterialTaxClassId: z.string().min(1),
    showZeroSalesMaterialPrice: z.boolean(),
};

const materialPurchaseFields = {
    purchaseMaterialQuantity: z.number(),
    purchaseMaterialUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options),
    purchaseMaterialPrice: z.number(),
    purchaseMaterialTaxClassId: z.string().min(1),
    showZeroPurchaseMaterialPrice: z.boolean(),
};

const vehicleClassField = {
    vehicleClass: VehicleClassSchema,
};

const transportSalesFields = {
    salesTransportQuantity: z.number(),
    salesTransportUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options),
    salesTransportPrice: z.number(),
    salesTransportTaxClassId: z.string().min(1),
    showZeroSalesTransportPrice: z.boolean(),
};

const transportPurchaseFields = {
    purchaseTransportQuantity: z.number(),
    purchaseTransportUnit: z.enum(AUTOMATIC_SUPPORTED_UNITS_SCHEMA.options),
    purchaseTransportPrice: z.number(),
    purchaseTransportTaxClassId: z.string().min(1),
    showZeroPurchaseTransportPrice: z.boolean(),
};

const francoFields = z.object({
    supplierOrganizationId: z.number().int().positive(),
    carrierOrganizationId: CarrierOrganizationIdSchema(),
    ...locationFields,
    ...vehicleClassField,
    ...materialSalesFields,
    ...materialPurchaseFields,
    ...transportPurchaseFields,
});

const merchantFields = z.object({
    supplierOrganizationId: z.number().int().positive(),
    ...locationFields,
    ...vehicleClassField,
    ...materialSalesFields,
    ...materialPurchaseFields,
});

const standardTransportOnlyFields = z.object({
    carrierOrganizationId: CarrierOrganizationIdSchema(),
    ...locationFields,
    ...vehicleClassField,
    ...transportSalesFields,
    ...transportPurchaseFields,
});

const standardMaterialAndTransportFields = z.object({
    supplierOrganizationId: z.number().int().positive(),
    carrierOrganizationId: CarrierOrganizationIdSchema(),
    ...locationFields,
    ...vehicleClassField,
    ...materialSalesFields,
    ...materialPurchaseFields,
    ...transportSalesFields,
    ...transportPurchaseFields,
});

type FrancoValues = AutomaticProductsFormValues & z.infer<typeof francoFields>;
type MerchantValues = AutomaticProductsFormValues & z.infer<typeof merchantFields>;
type StandardTransportOnlyValues = AutomaticProductsFormValues & z.infer<typeof standardTransportOnlyFields>;
type StandardMaterialAndTransportValues = AutomaticProductsFormValues &
    z.infer<typeof standardMaterialAndTransportFields>;

function assertFranco(values: AutomaticProductsFormValues): asserts values is FrancoValues {
    francoFields.parse(values);
}

function assertMerchant(values: AutomaticProductsFormValues): asserts values is MerchantValues {
    merchantFields.parse(values);
}

function assertStandardTransportOnly(
    values: AutomaticProductsFormValues
): asserts values is StandardTransportOnlyValues {
    standardTransportOnlyFields.parse(values);
}

function assertStandardMaterialAndTransport(
    values: AutomaticProductsFormValues
): asserts values is StandardMaterialAndTransportValues {
    standardMaterialAndTransportFields.parse(values);
}
